import React from 'react';
import { motion } from 'framer-motion';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

interface UnscheduledCampaign {
  id: string;
  title: string;
  description: string;
  type: string;
  validFrom: string;
  validTo: string;
  flyer: any;
  event: any;
  offerDetails: string;
  tagLine: string;
}

interface UnscheduledCampaignsCarouselProps {
  campaigns: UnscheduledCampaign[];
  loading: boolean;
  onCampaignClick: (campaignId: string, campaignType: string) => void;
  onViewMore: () => void;
}

const UnscheduledCampaignsCarousel: React.FC<UnscheduledCampaignsCarouselProps> = ({
  campaigns,
  loading,
  onCampaignClick,
  onViewMore
}) => {
  const scrollContainer = (direction: 'left' | 'right') => {
    const container = document.getElementById('manual-scroll');
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-medium text-[#EE4423] flex items-center gap-2">
          Un Scheduled Campaigns
        </h2>
        <button 
          onClick={onViewMore}
          className="text-gray-400 text-sm hover:text-[#EE4423] transition-colors"
        >
          View More
        </button>
      </div>

      {/* Horizontal Scrollable Cards */}
      <div className="relative">
        {/* Left Arrow - Only show if not loading and has multiple campaigns */}
        {!loading && campaigns.length > 2 && (
          <button
            onClick={() => scrollContainer('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800/80 p-2 rounded-full hover:bg-gray-700 transition-colors"
          >
            <MdChevronLeft size={24} className="text-white" />
          </button>
        )}

        <div
          id="manual-scroll"
          className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {loading ? (
            <div className="flex items-center justify-center w-full py-12">
              <div className="text-gray-400">Loading campaigns...</div>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="flex items-center justify-center w-full py-12">
              <div className="text-gray-400">No unscheduled campaigns found</div>
            </div>
          ) : (
            campaigns.map((campaign) => {
              // Get flyer image from campaign
              const flyerImage = campaign.flyer?.image?.storage_url || 
                                campaign.flyer?.mediaType?.[0]?.image?.storage_url || 
                                'https://via.placeholder.com/280x192?text=No+Image';
              
              // Format date
              const validFromDate = new Date(campaign.validFrom);
              const formattedDate = validFromDate.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              });

              return (
                <motion.div
                  key={campaign.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => onCampaignClick(campaign.id, campaign.type)}
                  className="flex-shrink-0 w-[280px] rounded-2xl overflow-hidden bg-gray-800/50 border border-gray-700/50 cursor-pointer hover:border-[#EE4423] transition-all"
                  style={{
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 0 14px 0 #0000001a'
                  }}
                >
                  {/* Campaign Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={flyerImage}
                      alt={campaign.title}
                      className="w-full h-full object-cover object-top"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/280x192?text=No+Image';
                      }}
                    />
                  </div>

                  {/* Campaign Info */}
                  <div className="p-4">
                    <h3 className="text-white font-medium text-xs mb-2 line-clamp-2">{campaign.title}</h3>
                    <p className="text-gray-400 text-[10px]">{formattedDate}</p>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Right Arrow - Only show if not loading and has multiple campaigns */}
        {!loading && campaigns.length > 2 && (
          <button
            onClick={() => scrollContainer('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800/80 p-2 rounded-full hover:bg-gray-700 transition-colors"
          >
            <MdChevronRight size={24} className="text-white" />
          </button>
        )}
      </div>
    </div>
  );
};

export default UnscheduledCampaignsCarousel;
